import axios from 'axios'
import type { AxiosInstance, AxiosResponse, AxiosRequestConfig, RawAxiosResponseHeaders, AxiosResponseHeaders } from 'axios'
import { message } from 'ant-design-vue'
import router from '@/router'
import { useUserStore } from '@/stores/user'

// 定义API响应类型
export interface ApiResponse<T = any> {
  success: boolean
  message: string
  data: T
}

// 定义文件下载响应类型
export interface BlobResponse extends Blob {
  headers?: RawAxiosResponseHeaders | AxiosResponseHeaders
}

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 60000
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 401错误处理状态管理
let isHandling401 = false

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 如果是blob类型（文件下载），直接返回blob对象和headers
    if (response.config.responseType === 'blob') {
      const blobResponse: BlobResponse = response.data
      blobResponse.headers = response.headers
      return blobResponse
    }
    
    // 返回完整的响应数据
    return response.data
  },
  (error) => {
    const originalRequest = error.config || {}
    const isLoginApi = originalRequest.url && originalRequest.url.includes('/auth/login')
    const token = localStorage.getItem('token')
    
    if (error.response) {
      switch (error.response.status) {
        case 401:
          if (isLoginApi) {
            // 登录接口，直接返回后端message，不弹token失效弹窗
            // 让页面自己处理message
            return Promise.reject(error.response.data)
          } else {
            // 处理401错误，避免并发请求重复处理
            if (!isHandling401) {
              isHandling401 = true
              
              // 显示错误提示
              message.error('登录已过期，请重新登录')
              
              // 清除所有用户状态
              const userStore = useUserStore()
              userStore.clearUser()
              
              // 清除localStorage中的所有认证相关信息
              localStorage.removeItem('token')
              localStorage.removeItem('userInfo')
              
              // 保存当前页面路径，登录成功后可以跳转回来
              const currentPath = router.currentRoute.value.fullPath
              if (currentPath !== '/login') {
                localStorage.setItem('redirectPath', currentPath)
              }
              
              // 跳转到登录页
              router.push('/login').finally(() => {
                // 重置401处理状态
                setTimeout(() => {
                  isHandling401 = false
                }, 1000)
              })
            }
          }
          break
        case 403:
          if (isLoginApi) {
            return Promise.reject(error.response.data)
          }
          message.error('权限不足')
          break
        case 400:
          // 处理400错误，显示后端返回的具体错误信息
          if (isLoginApi) {
            return Promise.reject(error.response.data)
          }
          message.error(error.response.data?.message || '请求参数错误')
          break
        default:
          message.error(error.response?.data?.message || '请求失败')
      }
    } else {
      message.error('网络连接失败')
    }
    return Promise.reject(error.response?.data || error)
  }
)

// 扩展request方法的类型
const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return service.get<any, ApiResponse<T>>(url, config)
  },
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return service.post<any, ApiResponse<T>>(url, data, config)
  },
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return service.put<any, ApiResponse<T>>(url, data, config)
  },
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return service.delete<any, ApiResponse<T>>(url, config)
  }
}

export default request